import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminHeader from "./admin-header";
import API from "../../../api";
import Loading from "../components/loading";
//import "./styles/gear-list-print.css";
import { createToast } from "./common";
import { handleApiError } from "./error-utils";
import { formatCentralTime } from "../../../common";

// Add print-specific styles
const printStyles = `
  .printable-area {
    display: none;
  }
  
  @media print {
    @page {
      margin: 0.5in;
      size: letter;
    }
    header, nav, footer, .non-printable {
      display: none !important;
    }
    .printable-area {
      display: block !important;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
    }
    body {
      margin: 0;
      padding: 0;
      background: white;
      font-family: 'Courier New', monospace;
      -webkit-print-color-adjust: exact;
    }
    .gear-form-page {
      page-break-after: always;
      padding: 10px;
      font-size: 9pt;
      line-height: 1.2;
      height: 100vh;
      box-sizing: border-box;
    }
    .gear-form-page:last-child {
      page-break-after: avoid;
    }
    .form-header {
      text-align: center;
      margin-bottom: 15px;
    }
    .form-header h2 {
      font-size: 14pt;
      font-weight: bold;
      margin: 0;
      text-decoration: underline;
      text-transform: none;
    }
    .form-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: 10px;
    }
    .top-section {
      margin-bottom: 5px;
      display: flex;
      justify-content: space-between;
      align-items: baseline;
    }
    .main-content {
      margin-top: 25px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto auto;
      column-gap: 15px;
      row-gap: 50px;
    }
    .rifle-section {
      grid-column: 1;
      grid-row: 1;
      display: flex;
      flex-direction: column;
      padding-top: 8px;
    }
    .scope-section {
      grid-column: 2;
      grid-row: 1;
      display: flex;
      flex-direction: column;
      padding-top: 8px;
    }
    .ammo-section {
      grid-column: 1;
      grid-row: 2;
      display: flex;
      flex-direction: column;
    }
    .notes-section-container {
      grid-column: 2;
      grid-row: 2;
      display: flex;
      flex-direction: column;
    }
    .form-field {
      margin: 4px 0;
      display: flex;
      align-items: baseline;
    }
    .form-field .label {
      font-weight: bold;
      min-width: 80px;
      margin-right: 8px;
      font-size: 12pt;
    }
    .fill-line {
      border-bottom: 1px solid black;
      min-width: 120px;
      height: 19px;
      flex: 1;
    }
    .fill-data {
      border-bottom: 1px solid black;
      min-width: 120px;
      padding-bottom: 1px;
      flex: 1;
      font-size: 12pt;
    }
    .section-divider {
      border-bottom: 1px solid black;
      font-weight: bold;
      text-align: center;
      margin: 5px 0 8px 0;
      padding: 2px;
      font-size: 14pt;
      text-transform: none;
    }
    .muzzle-velocity-section {
      margin: 5px 0;
    }
    .muzzle-velocity-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 15px;
      margin-top: 15px;
      margin-bottom: 10px;
      margin-left: 88px;
    }
    .muzzle-velocity-line {
      border-bottom: 1px solid black;
      height: 20px;
      min-width: 80px;
    }
    .notes-section {
      display: flex;
      flex-direction: column;
      flex: 1;
    }
    .client-notes {
      flex: 1;
      margin-bottom: 10px;
    }
    .instructor-notes {
      flex: 1;
    }
    .notes-line {
      border-bottom: 1px solid black;
      height: 15px;
      margin: 12px 0;
      width: 100%;
    }
    .notes-content {
      font-size: 12pt;
      margin-bottom: 5px;
      border-bottom: 1px solid black;
      padding-bottom: 2px;
      min-height: 15px;
    }
    .notes-subsection {
      margin-bottom: 8px;
    }
    .notes-label {
      font-weight: bold;
      font-size: 12pt;
      margin-bottom: 15px;
      text-decoration: underline;
    }
  }
`;

const GearListPrint = () => {
  const { dateId, classId } = useParams();
  const navigate = useNavigate();
  const [gearList, setGearList] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gearListLinkId, setGearListLinkId] = useState(null);
  const [gearListType, setGearListType] = useState("long_range"); // Default to long_range

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch gear list data
        const response = await API.get(`gear/admin/date/${dateId}`);
        if (response && response.gear_json) {
          setGearList(
            Array.isArray(response.gear_json) ? response.gear_json : []
          );

          // Save the gear list link ID if available
          if (response.gear_list_link_id) {
            setGearListLinkId(response.gear_list_link_id);
          }
        }

        // Fetch class and schedule info concurrently
        const [classResponse, scheduleResponse] = await Promise.all([
          API.get(`classes/${classId}`),
          API.get(`schedule/${dateId}`),
        ]);

        if (classResponse && scheduleResponse) {
          setClassInfo({
            ...classResponse[0],
            start_date: new Date(scheduleResponse[0].start_date),
            end_date: new Date(scheduleResponse[0].end_date),
          });

          // Set the gear list type if available
          if (classResponse[0].gear_list) {
            setGearListType(classResponse[0].gear_list);
          }
        }

        setLoading(false);
      } catch (error) {
        handleApiError(error, createToast, "Error fetching gear list");
        setLoading(false);
      }
    };

    fetchData();
  }, [dateId, classId]);

  const printPage = () => {
    window.print();
  };

  const goBackToSchedule = () => {
    navigate("/--admin-schedule");
  };

  // Generate the full gear list URL
  const gearListUrl = gearListLinkId
    ? `${window.location.origin}/gear-list/${gearListLinkId}`
    : null;

  // Helper function to format action type
  const formatAction = (action) => {
    switch (action) {
      case "bolt":
        return "Bolt Action";
      case "break":
        return "Break Barrel";
      case "semi_auto":
        return "Semi Auto";
      default:
        return "";
    }
  };

  // Helper function to format turret units
  const formatTurretUnits = (units) => {
    switch (units) {
      case "moa":
        return "MOA";
      case "iphy":
        return "IPHY";
      case "mill":
        return "MIL";
      default:
        return "";
    }
  };

  // Helper function to format focal plane
  const formatFocalPlane = (focalPlane) => {
    switch (focalPlane) {
      case "ffp":
        return "FFP (First Focal Plane)";
      case "sfp":
        return "SFP (Second Focal Plane)";
      case "fixed":
        return "Fixed Magnification";
      default:
        return "";
    }
  };

  if (loading) {
    return (
      <AdminHeader title="Gear List">
        <Loading />
      </AdminHeader>
    );
  }

  // Render blank form for hand-filling
  const renderBlankForm = () => {
    const blankEntry = {
      name: "",
      rifle: {},
      scope: {},
      ammo: {},
      comments: "",
    };

    return gearListType === "standard_rifle"
      ? renderStandardRifleFormEntry(blankEntry, "blank")
      : renderLongRangeFormEntry(blankEntry, "blank");
  };

  // Render long range form entry
  const renderLongRangeFormEntry = (entry, index) => {
    return (
      <div key={index} className="gear-form-page">
        <div className="form-content">
          <div className="top-section">
            <div className="form-field">
              <span className="label">Name:</span>
              <span className="fill-data">{entry.name || ""}</span>
            </div>
            <div className="form-field">
              <span className="label">Bench:</span>
              <span className="fill-line"></span>
            </div>
          </div>

          <div className="main-content">
            <div className="rifle-section">
              <div className="section-divider">Rifle</div>
              <div className="form-field">
                <span className="label">Make:</span>
                <span className="fill-data">
                  {entry.rifle?.manufacturer || ""}
                </span>
              </div>
              <div className="form-field">
                <span className="label">Model:</span>
                <span className="fill-data">{entry.rifle?.model || ""}</span>
              </div>
              <div className="form-field">
                <span className="label">Barrel Length:</span>
                <span className="fill-data">
                  {entry.rifle?.barrelLength
                    ? `${entry.rifle.barrelLength}"`
                    : ""}
                </span>
              </div>
              <div className="form-field">
                <span className="label">Twist:</span>
                <span className="fill-data">
                  {entry.rifle?.barrelTwist || ""}
                </span>
              </div>
              <div className="form-field">
                <span className="label">Action:</span>
                <span className="fill-data">
                  {formatAction(entry.rifle?.action) || ""}
                </span>
              </div>
            </div>

            <div className="scope-section">
              <div className="section-divider">Scope</div>
              <div className="form-field">
                <span className="label">Make:</span>
                <span className="fill-data">
                  {entry.scope?.manufacturer || ""}
                </span>
              </div>
              <div className="form-field">
                <span className="label">Model:</span>
                <span className="fill-data">{entry.scope?.model || ""}</span>
              </div>
              <div className="form-field">
                <span className="label">Elevation MOA/MIL:</span>
                <span className="fill-line"></span>
              </div>
              <div className="form-field">
                <span className="label">Elevation Units:</span>
                <span className="fill-data">
                  {formatTurretUnits(entry.scope?.turretUnits) || ""}
                </span>
              </div>
              <div className="form-field">
                <span className="label">Wind MOA/MIL:</span>
                <span className="fill-line"></span>
              </div>
              <div className="form-field">
                <span className="label">Wind Units:</span>
                <span className="fill-line"></span>
              </div>
              <div className="form-field">
                <span className="label">Sight Height:</span>
                <span className="fill-data">
                  {entry.scope?.boreAxis ? `${entry.scope.boreAxis}"` : ""}
                </span>
              </div>
              <div className="form-field">
                <span className="label">FFP/SFP:</span>
                <span className="fill-data">
                  {formatFocalPlane(entry.scope?.focalPlane) || ""}
                </span>
              </div>
            </div>

            <div className="ammo-section">
              <div className="section-divider">Ammo</div>
              <div className="form-field">
                <span className="label">Manufacturer:</span>
                <span className="fill-data">
                  {entry.ammo?.manufacturer || ""}
                </span>
              </div>
              <div className="form-field">
                <span className="label">Box Name:</span>
                <span className="fill-data">{entry.ammo?.boxName || ""}</span>
              </div>
              <div className="form-field">
                <span className="label">Box Number:</span>
                <span className="fill-data">{entry.ammo?.boxNumber || ""}</span>
              </div>
              <div className="form-field">
                <span className="label">Caliber:</span>
                <span className="fill-data">{entry.ammo?.caliber || ""}</span>
              </div>
              <div className="form-field">
                <span className="label">Weight:</span>
                <span className="fill-data">
                  {entry.ammo?.weight ? `${entry.ammo.weight} gr` : ""}
                </span>
              </div>
              <div className="form-field">
                <span className="label">G1/G7 BC:</span>
                <span className="fill-data">
                  {entry.ammo?.bcG1 ? `G1: ${entry.ammo.bcG1}` : ""}
                  {entry.ammo?.bcG1 && entry.ammo?.bcG7 ? " | " : ""}
                  {entry.ammo?.bcG7 ? `G7: ${entry.ammo.bcG7}` : ""}
                </span>
              </div>
              <div className="form-field">
                <span className="label">BC:</span>
                <span className="fill-line"></span>
              </div>
              <div className="muzzle-velocity-section">
                <div className="form-field">
                  <span className="label">Muzzle Velocity:</span>
                </div>
                <div className="muzzle-velocity-grid">
                  <div className="muzzle-velocity-line"></div>
                  <div className="muzzle-velocity-line"></div>
                  <div className="muzzle-velocity-line"></div>
                  <div className="muzzle-velocity-line"></div>
                  <div className="muzzle-velocity-line"></div>
                  <div className="muzzle-velocity-line"></div>
                </div>
              </div>
              <div className="form-field">
                <span className="label">SD:</span>
                <span className="fill-line"></span>
              </div>
            </div>

            <div className="notes-section-container">
              <div className="section-divider">Notes</div>
              <div className="notes-section">
                <div className="client-notes">
                  <div className="notes-label">Client Notes:</div>
                  {entry.comments && (
                    <div className="notes-content">{entry.comments}</div>
                  )}
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                </div>

                <div className="instructor-notes">
                  <div className="notes-label">Instructor Notes:</div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render standard rifle form entry
  const renderStandardRifleFormEntry = (entry, index) => {
    return (
      <div key={index} className="gear-form-page">
        <div className="form-content">
          <div className="top-section">
            <div className="form-field">
              <span className="label">Name:</span>
              <span className="fill-data">{entry.name || ""}</span>
            </div>
            <div className="form-field">
              <span className="label">Bench:</span>
              <span className="fill-line"></span>
            </div>
          </div>

          <div className="main-content">
            <div className="rifle-section">
              <div className="section-divider">Rifle</div>
              <div className="form-field">
                <span className="label">Rifle Style:</span>
                <span className="fill-data">
                  {entry.rifle?.manufacturer || ""}
                </span>
              </div>
            </div>

            <div className="scope-section">
              <div className="section-divider">Optic</div>
              <div className="form-field">
                <span className="label">Optic:</span>
                <span className="fill-data">
                  {entry.scope?.manufacturer || ""}
                </span>
              </div>
            </div>

            <div className="ammo-section">
              <div className="section-divider">Caliber</div>
              <div className="form-field">
                <span className="label">Caliber:</span>
                <span className="fill-data">{entry.ammo?.caliber || ""}</span>
              </div>
            </div>

            <div className="notes-section-container">
              <div className="section-divider">Notes</div>
              <div className="notes-section">
                <div className="client-notes">
                  <div className="notes-label">Client Notes:</div>
                  {entry.comments && (
                    <div className="notes-content">{entry.comments}</div>
                  )}
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                </div>

                <div className="instructor-notes">
                  <div className="notes-label">Instructor Notes:</div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                  <div className="notes-line"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Include print styles in the page */}
      <style>{printStyles}</style>

      {/* Non-printable header section */}
      <div className="non-printable">
        <AdminHeader>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h1 className="pbgt-yellow">
              Gear List for {classInfo?.class_title}
            </h1>
            <div>
              <button
                className="btn btn-secondary me-2"
                onClick={goBackToSchedule}
              >
                <i className="bi bi-arrow-left me-2"></i>Back to Schedule
              </button>
              <button className="btn btn-primary" onClick={printPage}>
                <i className="bi bi-printer me-2"></i>Print This Page
              </button>
            </div>
          </div>
          <div className="text-white">
            <p>
              <strong>Class Date:</strong>{" "}
              {formatCentralTime(classInfo?.start_date, {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
            <p>
              <strong>Total Entries:</strong> {gearList.length}
            </p>
            <p>
              <strong>Gear List Type:</strong>{" "}
              {gearListType === "standard_rifle"
                ? "Standard Rifle"
                : "Long Range"}
            </p>
            {gearListUrl && (
              <p>
                <strong>Gear List Link:</strong>{" "}
                <a
                  href={gearListUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white"
                >
                  {gearListUrl} <i className="bi bi-box-arrow-up-right"></i>
                </a>
                <button
                  className="btn btn-sm btn-outline-light ms-2"
                  onClick={() => {
                    navigator.clipboard.writeText(gearListUrl);
                    alert("Gear list link copied to clipboard!");
                  }}
                >
                  <i className="bi bi-clipboard"></i> Copy
                </button>
              </p>
            )}
          </div>
        </AdminHeader>

        {/* Web Preview Section */}
        <div className="container-fluid mt-4">
          <div className="row">
            <div className="col-12">
              <h2 className="text-center mb-4">Preview - Print Layout</h2>
              {gearList.length === 0 ? (
                <div className="alert alert-info text-center">
                  <p>No gear list entries found for this class date.</p>
                  <p className="mb-0">
                    A blank form will be available for printing.
                  </p>
                </div>
              ) : (
                gearList.map((entry, index) => (
                  <div key={index} className="card mb-4 shadow">
                    <div className="card-header bg-primary text-white">
                      <div className="row">
                        <div className="col-md-6">
                          <h5 className="mb-0">
                            <i className="bi bi-person-circle me-2"></i>
                            {entry.name}
                          </h5>
                        </div>
                        <div className="col-md-6 text-end">
                          <small>
                            Submitted:{" "}
                            {formatCentralTime(new Date(entry.date_submitted))}
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        {/* Rifle Section */}
                        <div className="col-md-6">
                          <div className="border rounded p-3 h-100 bg-light">
                            <h6 className="text-primary border-bottom pb-2 mb-3">
                              <i className="bi bi-crosshair me-2"></i>Rifle
                            </h6>
                            <div className="mb-2">
                              <strong>Make:</strong>{" "}
                              <span className="text-muted">
                                {entry.rifle?.manufacturer || "Not specified"}
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>Model:</strong>{" "}
                              <span className="text-muted">
                                {entry.rifle?.model || "Not specified"}
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>Barrel Length:</strong>{" "}
                              <span className="text-muted">
                                {entry.rifle?.barrelLength
                                  ? `${entry.rifle.barrelLength}"`
                                  : "Not specified"}
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>Twist:</strong>{" "}
                              <span className="text-muted">
                                {entry.rifle?.barrelTwist || "Not specified"}
                              </span>
                            </div>
                            <div className="mb-0">
                              <strong>Action:</strong>{" "}
                              <span className="text-muted">
                                {formatAction(entry.rifle?.action) ||
                                  "Not specified"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Scope Section */}
                        <div className="col-md-6">
                          <div className="border rounded p-3 h-100 bg-light">
                            <h6 className="text-success border-bottom pb-2 mb-3">
                              <i className="bi bi-bullseye me-2"></i>Scope
                            </h6>
                            <div className="mb-2">
                              <strong>Make:</strong>{" "}
                              <span className="text-muted">
                                {entry.scope?.manufacturer || "Not specified"}
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>Model:</strong>{" "}
                              <span className="text-muted">
                                {entry.scope?.model || "Not specified"}
                              </span>
                            </div>

                            <div className="mb-2">
                              <strong>Elevation Units:</strong>{" "}
                              <span className="text-muted">
                                {formatTurretUnits(entry.scope?.turretUnits) ||
                                  "Not specified"}
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>Magnification:</strong>{" "}
                              <span className="text-muted">
                                {entry.scope?.magMin && entry.scope?.magMax
                                  ? `${entry.scope.magMin}x - ${entry.scope.magMax}x`
                                  : "Not specified"}
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>Sight Height:</strong>{" "}
                              <span className="text-muted">
                                {entry.scope?.boreAxis
                                  ? `${entry.scope.boreAxis}"`
                                  : "Not specified"}
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>FFP/SFP:</strong>{" "}
                              <span className="text-muted">
                                {formatFocalPlane(entry.scope?.focalPlane) ||
                                  "Not specified"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Ammo Section */}
                        <div className="col-md-6">
                          <div className="border rounded p-3 h-100 bg-light">
                            <h6 className="text-warning border-bottom pb-2 mb-3">
                              <i className="bi bi-circle me-2"></i>Ammunition
                            </h6>
                            <div className="mb-2">
                              <strong>Manufacturer:</strong>{" "}
                              <span className="text-muted">
                                {entry.ammo?.manufacturer || "Not specified"}
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>Caliber:</strong>{" "}
                              <span className="text-muted">
                                {entry.ammo?.caliber || "Not specified"}
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>Weight:</strong>{" "}
                              <span className="text-muted">
                                {entry.ammo?.weight
                                  ? `${entry.ammo.weight} gr`
                                  : "Not specified"}
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>Box Name:</strong>{" "}
                              <span className="text-muted">
                                {entry.ammo?.boxName || "Not specified"}
                              </span>
                            </div>
                            <div className="mb-0">
                              <strong>G1/G7 BC:</strong>{" "}
                              <span className="text-muted">
                                {entry.ammo?.bcG1
                                  ? `G1: ${entry.ammo.bcG1}`
                                  : ""}
                                {entry.ammo?.bcG1 && entry.ammo?.bcG7
                                  ? " | "
                                  : ""}
                                {entry.ammo?.bcG7
                                  ? `G7: ${entry.ammo.bcG7}`
                                  : ""}
                                {!entry.ammo?.bcG1 && !entry.ammo?.bcG7
                                  ? "Not specified"
                                  : ""}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Notes Section */}
                        <div className="col-md-6">
                          <div className="border rounded p-3 h-100 bg-light">
                            <h6 className="text-info border-bottom pb-2 mb-3">
                              <i className="bi bi-chat-dots me-2"></i>Notes
                            </h6>
                            {entry.comments ? (
                              <div className="bg-white p-2 rounded border">
                                <strong>Client Notes:</strong>
                                <p className="mb-0 mt-1 text-muted">
                                  {entry.comments}
                                </p>
                              </div>
                            ) : (
                              <div className="text-muted fst-italic">
                                No client notes provided
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}

              <div className="alert alert-secondary mt-4">
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div>
                    <strong>Print Information:</strong> This will print{" "}
                    {gearList.length + 1} page(s) -
                    {gearList.length > 0
                      ? ` ${gearList.length} filled form(s) plus `
                      : " "}
                    1 blank form for hand-filling.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Printable content area */}
      <div className="printable-area">
        {gearList.length === 0 ? (
          renderBlankForm()
        ) : (
          <>
            {gearList.map((entry, index) =>
              gearListType === "standard_rifle"
                ? renderStandardRifleFormEntry(entry, index)
                : renderLongRangeFormEntry(entry, index)
            )}
            {renderBlankForm()}
          </>
        )}
      </div>
    </>
  );
};

export default GearListPrint;
